import axios from 'axios';
import { sortBy } from 'lodash';

import type { SupplierCompanyProfile } from '@sine/sine-connect-api-types';
import type {
  Acl,
  Site,
  Team,
  TeamCreateTeamForScReqBody,
  TeamDetailsWithLatLongReqBody,
  TeamWithBillingAndPermissions,
} from '@sine/sine-core-api-types';
import type { TeamWithRoles } from '@sine/team-membership-api-types';

import serializeQueryParams from '../../helpers/serializeQueryParams';
import { SineHeader } from '../../shared/types';
import {
  type AddTeamPermissionsParams,
  type CreateSupplierTeamProfileResponse,
  type GetTeamMetaSignupResponse,
  type ListAllResponse,
  type ListTeamPermissionsParams,
  type UpdateTeamPermissionsParams,
} from './team.types';

export class TeamService {
  async getUserOwnedTeam(userId: string) {
    const myTeams = await this.listTeams(true);
    // filtering by owner so teams must be TeamWithBillingAndPermissions
    return myTeams.find((team) => 'owner' in team && team.owner === userId) as
      | TeamWithBillingAndPermissions
      | undefined;
  }

  async listTeams(): Promise<Array<TeamWithBillingAndPermissions>>;
  async listTeams(includeNonAdminTeams: false): Promise<Array<TeamWithBillingAndPermissions>>;
  async listTeams(
    includeNonAdminTeams: true,
  ): Promise<Array<Pick<Team, 'name' | 'id'> | TeamWithBillingAndPermissions>>;
  async listTeams(includeNonAdminTeams = false): Promise<any> {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/team/my-teams`;

    const resp = await axios.get<Array<TeamWithBillingAndPermissions | Pick<Team, 'name' | 'id'>>>(url);
    const teams = resp.data;

    if (includeNonAdminTeams) {
      return teams;
    } else {
      return teams.filter((t) => 'billingContact' in t);
    }
  }

  async createTeam(props: TeamCreateTeamForScReqBody, region?: string) {
    const resp = await axios.post<Pick<Team, 'name' | 'id'>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/team/create/me`,
      props,
      region ? { headers: { [SineHeader.REGION_OVERRIDE]: region } } : undefined,
    );

    return resp.data;
  }

  async listAll() {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/team/all`;
    const resp = await axios.get<ListAllResponse>(url);
    return resp.data;
  }

  async getMyTeams() {
    const url = `${window.env.REACT_APP_SINE_TEAM_MEMBERSHIP_API_ENDPOINT}/role/my-teams`;
    const resp = await axios.get<Array<TeamWithRoles>>(url);

    return resp.data.map((t) => ({
      id: t.teamId,
      name: t.teamName,
    }));
  }

  async updateTeam(teamId: string, params: { name: string }) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/team/${teamId}/details`;

    const resp = await axios.post<Team>(url, params);
    return resp.data;
  }

  async deleteTeam(teamId: string) {
    const resp = await axios.delete(`${window.env.REACT_APP_SINE_CORE_API_URI}/team/${teamId}`);
    return resp.data;
  }

  async listSites(teamId?: string) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/team/${teamId}/sites`;
    const resp = await axios.get<Array<Site>>(url);
    return sortBy(resp.data, 'name');
  }

  async listPermissions(teamId: string, params: ListTeamPermissionsParams) {
    const qs = serializeQueryParams(params);

    try {
      const resp = await axios.get<Array<Acl>>(
        `${window.env.REACT_APP_SINE_CORE_API_URI}/permission/team/${teamId}?${qs}`,
      );
      return resp.data;
    } catch (err) {
      if ((err as { data?: { code?: string } }).data?.code === 'PERMISSION_DENIED') {
        return [];
      } else {
        throw err;
      }
    }
  }

  async addPermission(teamId: string, params: AddTeamPermissionsParams) {
    const resp = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/permission/team/${teamId}`, params);
    return resp.data;
  }

  async updatePermission(teamId: string, permissionId: string, params: UpdateTeamPermissionsParams) {
    const resp = await axios.post(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/permission/team/${teamId}/${permissionId}`,
      params,
    );
    return resp.data;
  }

  async removePermission(teamId: string, permissionId: string) {
    const resp = await axios.delete(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/permission/team/${teamId}/${permissionId}`,
    );
    return resp.data;
  }

  async acceptPermissions(teamId: string, userId: string) {
    const resp = await axios.post(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/permission/status/accept/${teamId}?userId=${userId}`,
      undefined,
      {
        headers: { [SineHeader.X_SINE_TEAM_ID]: teamId },
      },
    );
    return resp.data;
  }

  async acceptUnclaimedAccountPermission(
    teamId: string,
    { otaToken, accountVerify }: { otaToken: string; accountVerify: string },
  ) {
    const resp = await axios.post(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/permission/status/accept/${teamId}?otaToken=${otaToken}&accountVerify=${accountVerify}`,
      undefined,
      {
        headers: { [SineHeader.X_SINE_TEAM_ID]: teamId },
      },
    );
    return resp.data;
  }

  async resendPermission(teamId: string, permissionId: string) {
    const resp = await axios.post(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/permission/team/${teamId}/${permissionId}/resend`,
    );
    return resp.data;
  }

  async getDefaultCountryCode(teamId: string) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/team/${teamId}/country-code`;

    const resp = await axios.get(url);
    return resp.data;
  }

  /**
   * fetchTeamPermissions for user. TeamId is optional due to Team Switcher
   * already setting header and value when in TS mode. TeamId is required when not in TS
   * mode
   * @param {string} teamId Optional teamId
   * @returns Team permissions array
   */
  async fetchTeamPermissions(teamId?: string) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/team/permissions`;

    try {
      const resp = await axios.get(url, {
        ...(teamId && { headers: { [SineHeader.X_SINE_TEAM_ID]: teamId } }),
      });
      return resp.data;
    } catch (error: any) {
      if (error?.data?.code === 'FORBIDDEN') {
        return [];
      } else {
        throw error;
      }
    }
  }

  /**
   * Check if there's any active collaborator with role for current TS's team
   * @param {string} role role to check
   * @returns boolean\
   */
  async isActiveRolePresent(role: string) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/permission/role/${role}/is-active`;
    const resp = await axios.get<boolean>(url);
    return resp.data;
  }

  async getSupplierTeamProfile(token: string) {
    const res = await axios.get<SupplierCompanyProfile>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/team/profile/${token}`,
    );

    return res.data;
  }

  async createSupplierTeamProfile(teamProfile: TeamDetailsWithLatLongReqBody, token: string) {
    const res = await axios.post<CreateSupplierTeamProfileResponse>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/team/profile/create/${token}`,
      teamProfile,
    );

    return res.data;
  }

  async getTeamMetadata(teamId: string) {
    const res = await axios.get<GetTeamMetaSignupResponse>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/team/team-meta-signup`,
      {
        headers: { [SineHeader.X_SINE_TEAM_ID]: teamId },
      },
    );
    return res.data;
  }
}

const teamService = new TeamService();
export default teamService;
